import _ from "lodash"

import {LoadingStatus} from "@/mixins/AuthorizationHelpers/LoadingStatus";
import axios from "axios";

export class UseRootProperty {
    constructor(mixin, apiLookupRoute, rootKey) {
        this.mixin = mixin
        this.rootKey = rootKey
        this.rootMetaKey = `${rootKey}_meta`
        this.apiLookupRoute = apiLookupRoute
        this.listeners = {}

        // Ensure that the object for this property exists on root.
        // If you want this root property to be properly reactive w/ Vue, it must be declared in the app.js file where
        // the vue instance is initiated.
        if (_.isNil(this.mixin.$root[this.rootKey])) {
            this.mixin.$root[this.rootKey] = {}
        }

        // Make sure that the metadata exists for the property on root.
        if (_.isNil(this.mixin.$root[this.rootMetaKey])) {
            this.mixin.$root[this.rootMetaKey] = {}
        }
    }

    addListener(key, action, company = null) {
        if (this._isNotReady()) {
            return false
        }

        company = this.mixin.currentCompanyIfNull(company)
        this._prepareCompany(company)
        this.listeners[company][key] = action

        // Add Listener to root
        this._addListenerToRoot(company, key, action)
    }

    loadCompany(company = null) {
        if (this._isNotReady() || !this._shouldLoadCompany(company)) {
            return false
        }

        company = this.mixin.currentCompanyIfNull(company)
        this._setCompanyStatus(LoadingStatus.LOADING, company)

        // This will make it so that if hasFeature is called it will automatically attempt to register
        // an auto-update listener for the component & company.
        this.mixin.autoUpdateOnCoreChanges(company)

        // TODO. For now just return a Promise that sends the data.
        return new Promise((resolve, reject) => {
            axios.get(this.mixin.company.buildCompanyApiInternalRoute(this.apiLookupRoute, company)
            ).then(response => {
                this._setRootProperty(response.data, company)
                resolve()
            }).catch(error => {
                this._setCompanyStatus(LoadingStatus.ERROR_LOADING, company)
                reject()
            })
        })
    }

    _getRootProperty(company = null) {
        if (this._isNotReady()) {
            return false
        }

        company = this.mixin.currentCompanyIfNull(company)
        this.loadCompany(company)

        // Verify the company has an array and return the company's array
        this._prepareCompany(company)
        return this.mixin.$root[this.rootKey][company]
    }

    _setRootProperty(property, company) {
        this._prepareCompany(company)

        this._setCompanyStatus(LoadingStatus.LOADED, company)
        this.mixin.$root[this.rootKey][company] = property

        this._updateListeners(company)
    }

    _addListenerToRoot(company, key, action) {
        this._initRootMetaIfNot(company)
        this.mixin.$root[this.rootMetaKey][company]['listeners'][key] = action
    }

    _updateListeners (company) {
        _.forEach(this.mixin.$root[this.rootMetaKey][company]['listeners'], (action, key) => {
            action(company)
        })
    }

    _prepareCompany(company) {
        if (!(company in this.mixin.$root[this.rootKey])) {
            // Company has not loaded any data before
            this.mixin.$root[this.rootKey][company] = {name: 'n/a',}
            this.mixin.$root[this.rootKey][company][this.rootKey] = []
        }

        if (!(company in this.listeners)) {
            // Company has not loaded any data before
            this.listeners[company] = {}
        }
    }

    _shouldLoadCompany(company) {
        // We should attempt to load a company if we are not already trying
        return this._getCompanyStatus(company) === LoadingStatus.NOT_LOADED
    }

    // Return true if mixin, $root, or the permissions array are nil.
    // I don't ever really care if we are ready, I will only test if we are not ready and return anything as false.
    _isNotReady() {
        return _.isNil(this.mixin) || _.isNil(this.mixin.$root) || _.isNil(this.mixin.$root.permissions)
    }

    _setCompanyStatus(status, company = null) {
        company = this.mixin.currentCompanyIfNull(company)

        // Update the Companies Status on $root
        this._initRootMetaIfNot(company)
        this.mixin.$root[this.rootMetaKey][company]['companyStatus'] = status
    }

    _getCompanyStatus(company = null) {
        company = this.mixin.currentCompanyIfNull(company)

        // Update the Companies Status on $root
        this._initRootMetaIfNot(company)
        return this.mixin.$root[this.rootMetaKey][company]['companyStatus']
    }

    _initRootMetaIfNot(company) {
        company = this.mixin.currentCompanyIfNull(company)
        if (!_.isNil(this.mixin.$root[this.rootMetaKey][company])) {
            return
        }

        this.mixin.$root[this.rootMetaKey][company] = {
                listeners: {},
                companyStatus: LoadingStatus.NOT_LOADED,
            }
    }
}
